import { filter } from '@atlaskit/adf-utils/traverse';

import type {
	PreloadFailureReason,
	PreloadFailureResult,
} from '@confluence/fabric-extension-lib/entry-points/fabric-extension-lib-types.ts';
import { supportedExtensions } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { getMultiMacroQueryBlocklistFF } from '@confluence/fabric-extension-queries/entry-points/useMultiMacroQuery.ts';
import {
	MultiMacroQueryQuery,
	type multiMacroQueryQuery$data,
	type multiMacroQueryQuery$variables,
} from '@confluence/fabric-extension-queries/entry-points/multiMacroQueryQuery.ts';
import {
	MultiMacroQueryWithAppKeyQuery,
	type multiMacroQueryWithAppKeyQuery$data,
	type multiMacroQueryWithAppKeyQuery$variables,
} from '@confluence/fabric-extension-queries/entry-points/multiMacroQueryWithAppKeyQuery.ts';
import {
	SLOW_QUERY_DEFAULT_TIMEOUT,
	slowQueryGuard,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';

import { DEFAULT_PRELOAD_COUNT } from './preloader-constants.ts';
import { preloadStaticWRM } from './preloadStaticWRM.ts';

const extractExtensions = (adf: any) => filter(adf, (node) => supportedExtensions.has(node.type));

export const hasExtensions = (contentNodes: any) => {
	try {
		const adf = JSON.parse(contentNodes[0].body.dynamic.value);
		const extensions = extractExtensions(adf);
		return extensions.length > 0;
	} catch {
		return false;
	}
};

const failureIfNotSuccess = (reason: PreloadFailureReason) => (success: boolean) =>
	success ? undefined : { MacrosQuery_extensions: reason };

const preloadMacrosSSRAndReturnTopXFailed = async (
	contentId: string,
	contentNodesOrPromise?: any,
	isInitialLoad?: boolean,
) => {
	const blocklist = getMultiMacroQueryBlocklistFF();

	const isContentNodePromise = typeof contentNodesOrPromise?.then === 'function';
	if (contentNodesOrPromise && !isContentNodePromise) {
		if (!hasExtensions(contentNodesOrPromise)) {
			return {};
		}
	}

	const noExtensionsSignal = new Promise<void>((resolve) => {
		if (isContentNodePromise)
			contentNodesOrPromise.then((contentNodes: any) => {
				if (!hasExtensions(contentNodes)) {
					resolve();
				}
			});
	});

	// NOTE: prealod is not a type. It was created with a typo https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/dynamic_configs/confluence_frontend_prealod_macro_timeout
	const macroTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_prealod_macro_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	const macrosQueryTask = () =>
		slowQueryGuard(
			'MacrosQuery_preload',
			() => {
				if (expValEquals('confluence_fetch_appkey_field', 'isEnabled', true)) {
					return query<
						multiMacroQueryWithAppKeyQuery$data,
						multiMacroQueryWithAppKeyQuery$variables
					>({
						variables: {
							first: DEFAULT_PRELOAD_COUNT,
							contentId,
							blocklist,
						},
						query: MultiMacroQueryWithAppKeyQuery,
						context: { single: true },
					});
				}
				return query<multiMacroQueryQuery$data, multiMacroQueryQuery$variables>({
					variables: {
						first: DEFAULT_PRELOAD_COUNT,
						contentId,
						blocklist,
					},
					query: MultiMacroQueryQuery,
					context: { single: true },
				});
			},
			macroTimeout,
			noExtensionsSignal,
		).then(failureIfNotSuccess('timeout-rendered-macros'));

	// If confluence_preload_default_wrm_css is enabled and this is the initial load,
	// run WRM in parallel with MacrosQuery. Otherwise, use original behavior (MacrosQuery only)
	if (isInitialLoad && expValEquals('confluence_preload_default_wrm_css', 'isEnabled', true)) {
		const wrmTask = slowQueryGuard(
			'ssr-app/preloadStaticWRM',
			() => preloadStaticWRM(),
			SLOW_QUERY_DEFAULT_TIMEOUT,
			noExtensionsSignal,
		);

		let macrosResult: ReturnType<ReturnType<typeof failureIfNotSuccess>> | undefined;
		const macrosTask = macrosQueryTask().then((result) => {
			macrosResult = result;
		});

		await waitForAllReturnWhenError([macrosTask, wrmTask]);

		return macrosResult ? Object.assign({}, ...[macrosResult]) : {};
	}

	const preloadResult = await macrosQueryTask();

	return preloadResult ? Object.assign({}, ...[preloadResult]) : {};
};

export async function preloadMacrosSSRMultiple(
	contentId: string,
	contentNodesOrPromise?: any,
	isInitialLoad?: boolean,
): Promise<PreloadFailureResult | undefined> {
	const topXFailed = await preloadMacrosSSRAndReturnTopXFailed(
		contentId,
		contentNodesOrPromise,
		isInitialLoad,
	);
	window['__SSR_MACRO_PRELOAD_TOPX_FAILS__'] = topXFailed;

	return topXFailed;
}
